/**
 * Service provider categories — used for user search and
 * “Become a provider” selection (same taxonomy).
 * Grouped for optgroup dropdowns.
 */

export const SERVICE_PROVIDER_CATEGORY_GROUPS = [
  {
    id: 'trades',
    label: 'The trades',
    categories: [
      { id: 'plumbing', label: 'Plumbing' },
      { id: 'electrical', label: 'Electrical' },
      { id: 'ac_hvac', label: 'AC / HVAC' },
      { id: 'carpentry', label: 'Carpentry' },
      { id: 'masonry', label: 'Masonry' },
      { id: 'painting', label: 'Painting' },
      { id: 'tiling', label: 'Tiling' },
      { id: 'roofing', label: 'Roofing' },
      { id: 'welding', label: 'Welding' },
      { id: 'appliance_repair', label: 'Appliance repair' },
      { id: 'handyman', label: 'Handyman' },
      { id: 'solar', label: 'Solar install' },
      { id: 'water_tank', label: 'Water tank / pump' },
      { id: 'generator', label: 'Generator service' },
      { id: 'glass', label: 'Glass / windows' },
      { id: 'locksmith', label: 'Locksmith' },
      { id: 'furniture', label: 'Furniture assembly' },
      { id: 'upholstery', label: 'Upholstery' },
      { id: 'auto_mechanic', label: 'Auto mechanic' },
      { id: 'auto_body', label: 'Auto body / dent repair' },
      { id: 'tyre_battery', label: 'Tyre & battery' },
      { id: 'tow_truck', label: 'Tow truck' },
    ],
  },
  {
    id: 'labour',
    label: 'Labour',
    categories: [
      { id: 'moving', label: 'Moving / labour' },
      { id: 'cleaning_home', label: 'Home cleaning' },
      { id: 'cleaning_office', label: 'Office cleaning' },
      { id: 'laundry', label: 'Laundry / ironing' },
      { id: 'gardening', label: 'Gardening / landscaping' },
      { id: 'pest_control', label: 'Pest control' },
      { id: 'security', label: 'Security / watchman' },
    ],
  },
  {
    id: 'health',
    label: 'Health',
    categories: [
      { id: 'fitness', label: 'Fitness / personal trainer' },
      { id: 'massage', label: 'Massage / spa' },
      { id: 'childcare', label: 'Childcare / babysitting' },
      { id: 'eldercare', label: 'Elder care' },
      { id: 'pet_care', label: 'Pet care / walking' },
    ],
  },
  {
    id: 'beauty',
    label: 'Beauty',
    categories: [
      { id: 'barber', label: 'Barber' },
      { id: 'hair_stylist', label: 'Hair stylist' },
      { id: 'nails_beauty', label: 'Nails / beauty' },
      { id: 'makeup', label: 'Makeup artist' },
      { id: 'sewing', label: 'Sewing / alterations' },
      { id: 'fashion_design', label: 'Fashion design' },
    ],
  },
  {
    id: 'food_events',
    label: 'Food & events',
    categories: [
      { id: 'catering', label: 'Catering' },
      { id: 'baking', label: 'Baking / cakes' },
      { id: 'chef_private', label: 'Private chef' },
      { id: 'event_planning', label: 'Event planning' },
      { id: 'decor', label: 'Event décor' },
      { id: 'photography', label: 'Photography' },
      { id: 'videography', label: 'Videography' },
      { id: 'dj_sound', label: 'DJ / sound' },
    ],
  },
  {
    id: 'technology',
    label: 'Technology',
    categories: [
      { id: 'phone_repair', label: 'Phone / electronics repair' },
      { id: 'computer_it', label: 'Computer / IT support' },
      { id: 'web_dev', label: 'Web / app development' },
      { id: 'graphic_design', label: 'Graphic design' },
      { id: 'printing', label: 'Printing / signage' },
      { id: 'cctv', label: 'CCTV / networking' },
      { id: 'digital_marketing', label: 'Digital marketing / social' },
      { id: 'virtual_assist', label: 'Virtual assistant (online)' },
    ],
  },
  {
    id: 'professionals',
    label: 'Professionals',
    categories: [
      { id: 'tutoring', label: 'Tutoring' },
      { id: 'online_tutoring', label: 'Online tutoring' },
      { id: 'music_lessons', label: 'Music lessons' },
      { id: 'legal_notary', label: 'Legal / notary help' },
      { id: 'real_estate', label: 'Real estate agent' },
      { id: 'consulting', label: 'Consulting (remote OK)' },
    ],
  },
  {
    id: 'finance',
    label: 'Finance',
    categories: [
      { id: 'accounting', label: 'Bookkeeping / accounting' },
      { id: 'crypto_help', label: 'Crypto / wallet help' },
    ],
  },
  {
    id: 'other',
    label: 'Other',
    categories: [{ id: 'other', label: 'Other (describe)' }],
  },
];

/** Flat list (legacy / filters). */
export const SERVICE_PROVIDER_CATEGORIES = SERVICE_PROVIDER_CATEGORY_GROUPS.flatMap(
  g => g.categories
);

export const getCategoryLabel = id =>
  SERVICE_PROVIDER_CATEGORIES.find(c => c.id === id)?.label || id;

/** Demo providers for browse / search (tablet UX). */
export const DEMO_SERVICE_PROVIDERS = [
  {
    id: 'sp-1',
    name: 'Marcus Plumbing Co.',
    categoryId: 'plumbing',
    area: 'Kingston · Portmore',
    blurb: 'Leaks, installs, emergencies. Same-day when available.',
    badge: 'Available',
    image: '/static/demo/plumbing.jpg',
    rateHint: 'From 6,500 / visit',
    deliveryModes: ['on_site'],
    settlement: ['cash', 'bank_transfer', 'mobile_wallet'],
    portfolio: ['Emergency leak repair', 'Water heater install', 'Bathroom re-pipe'],
    etaHint: 'Today 2–5 pm',
    quoteNote: 'Includes basic parts check. Licensed.',
    years: 8,
    verifiedPhone: true,
    completionCount: 24
  },
  {
    id: 'sp-2',
    name: 'Bright Volt Electrical',
    categoryId: 'electrical',
    area: 'St. Andrew',
    blurb: 'Panels, outlets, light fixtures. Licensed.',
    badge: 'Available',
    image: '/static/demo/electrical.jpg',
    rateHint: 'From 8,000 / call-out',
    deliveryModes: ['on_site'],
    settlement: ['cash', 'bank_transfer', 'stablecoin'],
    portfolio: ['Panel upgrade', 'Outlet circuit', 'LED retrofit'],
    etaHint: 'Tomorrow morning',
    quoteNote: 'Quote covers labour; materials at cost.',
    years: 12,
    verifiedPhone: true,
    completionCount: 36
  },
  {
    id: 'sp-3',
    name: 'Cool Breeze HVAC',
    categoryId: 'ac_hvac',
    area: 'Spanish Town',
    blurb: 'AC service & install. Residential and small office.',
    badge: 'Busy today',
    image: '/static/demo/hvac.jpg',
    rateHint: 'Service from 7,500',
    deliveryModes: ['on_site'],
    settlement: ['cash', 'mobile_wallet', 'card'],
    portfolio: ['Split AC service', 'New install', 'Duct clean'],
    etaHint: 'Within 48h',
    quoteNote: 'Can bring common filters.',
    years: 6,
    verifiedPhone: true,
    completionCount: 18
  },
  {
    id: 'sp-4',
    name: 'Sparkle Home Clean',
    categoryId: 'cleaning_home',
    area: 'Half Way Tree',
    blurb: 'Deep clean & weekly plans. Bring supplies.',
    badge: 'Available',
    image: '/static/demo/cleaning.jpg',
    rateHint: 'From 5,000 / session',
    deliveryModes: ['on_site'],
    settlement: ['cash', 'mobile_wallet', 'stablecoin'],
    portfolio: ['Deep clean', 'Move-out clean', 'Weekly plan'],
    etaHint: 'Tomorrow morning',
    quoteNote: 'Supplies included unless you prefer yours.',
    years: 4,
    verifiedPhone: false,
    completionCount: 12
  },
  {
    id: 'sp-5',
    name: 'YardPro Landscaping',
    categoryId: 'gardening',
    area: 'St. Catherine',
    blurb: 'Cut, trim, clear lots. Weekend slots open.',
    badge: 'Available',
    image: '/static/demo/garden.jpg',
    rateHint: 'From 4,500 / yard',
    deliveryModes: ['on_site'],
    settlement: ['cash', 'bank_transfer'],
    portfolio: ['Lawn cut', 'Lot clear', 'Hedge trim'],
    etaHint: 'This weekend',
    quoteNote: 'Bring own tools; debris haul extra.',
    years: 9,
    verifiedPhone: true,
    completionCount: 27
  },
  {
    id: 'sp-6',
    name: 'TechFix Mobiles',
    categoryId: 'phone_repair',
    area: 'New Kingston',
    blurb: 'Screens, batteries, water damage assessment.',
    badge: 'Walk-in',
    image: '/static/demo/phonerepair.jpg',
    rateHint: 'Screens from 9,000',
    deliveryModes: ['on_site', 'shop'],
    settlement: ['cash', 'card', 'mobile_wallet'],
    portfolio: ['Screen replace', 'Battery', 'Diagnostics'],
    etaHint: 'Same day if parts in stock',
    quoteNote: 'Shop drop-off or on-site for businesses.',
    years: 5,
    verifiedPhone: true,
    completionCount: 15
  },
  {
    id: 'sp-7',
    name: 'Ms. Anita Tutoring',
    categoryId: 'tutoring',
    area: 'Online · Kingston',
    blurb: 'CXC math & English. Evening sessions.',
    badge: 'Available',
    image: '/static/demo/tutoring.jpg',
    rateHint: '2,500 / hour',
    deliveryModes: ['remote_online', 'on_site'],
    settlement: ['mobile_wallet', 'stablecoin', 'bank_transfer'],
    portfolio: ['CXC Math', 'English Lit', 'Online Zoom'],
    etaHint: 'Evenings this week',
    quoteNote: 'Online preferred; first session assessment.',
    years: 11,
    verifiedPhone: true,
    completionCount: 33
  },
  {
    id: 'sp-8',
    name: 'Cut & Fade Barbers',
    categoryId: 'barber',
    area: 'Downtown',
    blurb: 'Walk-ins and appointments. Kids welcome.',
    badge: 'Open',
    image: '/static/demo/barber.jpg',
    rateHint: 'Cuts from 1,800',
    deliveryModes: ['shop'],
    settlement: ['cash', 'mobile_wallet'],
    portfolio: ['Fades', 'Kids cuts', 'Beard trim'],
    etaHint: 'Walk-in today',
    quoteNote: 'Book a chair time to skip the queue.',
    years: 7,
    verifiedPhone: true,
    completionCount: 21
  },
  {
    id: 'sp-9',
    name: 'MoveRight Labour',
    categoryId: 'moving',
    area: 'Island-wide (base Kingston)',
    blurb: 'Helpers + truck by request. Hourly rates.',
    badge: 'Available',
    image: '/static/demo/moving.jpg',
    rateHint: 'From 3,500 / helper / hr',
    deliveryModes: ['on_site'],
    settlement: ['cash', 'bank_transfer', 'locked_escrow'],
    portfolio: ['Apartment move', 'Office desks', 'Load only'],
    etaHint: 'Within 48h',
    quoteNote: 'Truck optional — ask in quote.',
    years: 3,
    verifiedPhone: false,
    completionCount: 9
  },
  {
    id: 'sp-10',
    name: 'Lens & Light Studio',
    categoryId: 'photography',
    area: 'Travel fee applies',
    blurb: 'Events, portraits, product shots.',
    badge: 'Book ahead',
    image: '/static/demo/photo.jpg',
    rateHint: 'Events from 25,000',
    deliveryModes: ['on_site', 'remote_online'],
    settlement: ['bank_transfer', 'stablecoin', 'card'],
    portfolio: ['Weddings', 'Product', 'Portraits'],
    etaHint: 'Next week',
    quoteNote: 'Edited gallery delivered digitally.',
    years: 10,
    verifiedPhone: true,
    completionCount: 30
  },
  {
    id: 'sp-11',
    name: 'PixelCraft Studio',
    categoryId: 'web_dev',
    area: 'Online island-wide',
    blurb: 'Landing pages, store fixes, light apps. Remote-first.',
    badge: 'Remote',
    image: '/static/demo/coworking.jpg',
    rateHint: 'From 15,000 / day or milestone',
    deliveryModes: ['remote_online'],
    settlement: ['stablecoin', 'locked_escrow', 'bank_transfer'],
    portfolio: ['Landing pages', 'Checkout fixes', 'API wiring'],
    etaHint: 'Kickoff within 48h',
    quoteNote: 'Milestone escrow preferred for larger builds.',
    years: 6,
    verifiedPhone: true,
    completionCount: 18
  },
  {
    id: 'sp-12',
    name: 'VA North Desk',
    categoryId: 'virtual_assist',
    area: 'Online',
    blurb: 'Inbox, scheduling, light research. Hourly retainers.',
    badge: 'Online',
    image: '/static/demo/tutoring.jpg',
    rateHint: '1,800 / hour',
    deliveryModes: ['remote_online'],
    settlement: ['stablecoin', 'mobile_wallet', 'bank_transfer'],
    portfolio: ['Inbox triage', 'Calendar', 'Research briefs'],
    etaHint: 'Start tomorrow',
    quoteNote: 'Remote only — async + daily check-in.',
    years: 2,
    verifiedPhone: false,
    completionCount: 6
  },
];

export const filterDemoProviders = (query, categoryId) => {
  const q = (query || '').trim().toLowerCase();
  return DEMO_SERVICE_PROVIDERS.filter(p => {
    const catLabel = getCategoryLabel(p.categoryId).toLowerCase();
    const matchesCat = !categoryId || categoryId === 'all' || p.categoryId === categoryId;
    if (!matchesCat) {
      return false;
    }
    if (!q) {
      return true;
    }
    return (
      p.name.toLowerCase().includes(q) ||
      catLabel.includes(q) ||
      p.area.toLowerCase().includes(q) ||
      p.blurb.toLowerCase().includes(q)
    );
  });
};

export default SERVICE_PROVIDER_CATEGORIES;
